import { type RefObject } from 'react';
import Checkbox from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import ArrowDownIcon from '@/storybook/icons/ArrowDownIcon';
import type { CheckState } from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import type { ChangeEvent, ReactNode } from 'react';
import { TREE_ITEM_TOOLTIP_ID } from '@/storybook/components/FormCollection/components/constants';
import styles from './TreeItem.module.css';

type TreeItemCaretSlotProps = {
  showCaretColumn: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  isDisabled: boolean;
  caretButtonClassName: string;
  label: string;
  onCaretClick: (e: React.MouseEvent) => void;
  caretDataTestId?: string;
};

export function TreeItemCaretSlot({
  showCaretColumn,
  hasChildren,
  isExpanded,
  isDisabled,
  caretButtonClassName,
  label,
  onCaretClick,
  caretDataTestId,
}: TreeItemCaretSlotProps) {
  if (!showCaretColumn) {
    return null;
  }
  return (
    <div className={styles.caretSlot}>
      {hasChildren && (
        <button
          type="button"
          className={caretButtonClassName}
          onClick={onCaretClick}
          tabIndex={-1}
          aria-label={isExpanded ? `Collapse ${label}` : `Expand ${label}`}
          disabled={isDisabled}
          data-testid={caretDataTestId}
        >
          <ArrowDownIcon className={styles.caretIcon} aria-hidden />
        </button>
      )}
    </div>
  );
}

type TreeItemCheckboxSlotProps = {
  showCheckbox: boolean;
  checked: CheckState;
  onCheck?: (e?: ChangeEvent<HTMLInputElement>) => void;
  isDisabled: boolean;
  checkboxDataTestId?: string;
};

export function TreeItemCheckboxSlot({
  showCheckbox,
  checked,
  onCheck,
  isDisabled,
  checkboxDataTestId,
}: TreeItemCheckboxSlotProps) {
  if (!showCheckbox) {
    return null;
  }
  const handleCheckboxAreaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  return (
    <div className={styles.checkboxWrapper} onClick={handleCheckboxAreaClick}>
      <Checkbox
        label=""
        checked={checked}
        onChange={onCheck}
        disableFocus
        disabled={isDisabled}
        className={styles.checkbox}
        data-testid={checkboxDataTestId}
      />
    </div>
  );
}

type TreeItemLabelRegionProps = {
  labelRef: RefObject<HTMLSpanElement | null>;
  labelId: string;
  label: string;
  labelContent: ReactNode | undefined;
  tooltipText: string | undefined;
  isTruncated: boolean;
};

export function TreeItemLabelRegion({
  labelRef,
  labelId,
  label,
  labelContent,
  tooltipText,
  isTruncated,
}: TreeItemLabelRegionProps) {
  return (
    <span
      ref={labelRef}
      id={labelId}
      className={styles.label}
      data-tooltip-id={TREE_ITEM_TOOLTIP_ID}
      data-tooltip-content={tooltipText ?? label}
      data-tooltip-hidden={!isTruncated}
    >
      {labelContent ?? label}
    </span>
  );
}
